import React, { useCallback } from 'react'
import { Divider, Separator, colors, Ellipsis, DataBlock, Paragraph, H3, Skeleton } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { i18n } from '@fs/zion-locale'
import { gql, useQuery } from '@apollo/client'

export const wikiLinkQuery = gql`
  query getWikiArticle($placeIds: [ID]) {
    wikiLinks(placeIds: $placeIds) {
      title
      description
      url
    }
  }
`

const cardCss = css`
  background-color: ${colors.gray02};
  border-radius: 5px;
  border: none;
  padding: var(--spacer-nano) var(--spacer-xs);
  text-decoration: underline;
  color: inherit;
  display: block;
  * {
    color: ${colors.blue60};
  }
  &:hover {
    background-color: ${colors.gray03};
  }

  &:focus-within,
  &:focus {
    background-color: ${colors.gray03};
    outline: 2px solid ${colors.blue60};
    outline-offset: 2px;
  }
  p {
    color: ${colors.gray100};
  }
`
const greyText = css`
  color: ${colors.gray02};
`
const transl = {
  heading: () => i18n.t('wikiLink.heading', 'Research Wiki'),
  subHeading: () => i18n.t('wikiLink.subHeading', 'Research articles may provide additional context for your search.'),
  errorSubtitle: () =>
    i18n.t(
      'wikiLink.errorSubtitle',
      'It looks like there was a problem finding a wiki article that matches your search. Please try again later.'
    ),
  noWikiArticle: () => i18n.t('wikiLink.noWikiArticle', 'No wiki article found.'),
}

function WikiCard({ wikiData }) {
  const { url, title, description } = wikiData?.wikiLinks?.[0] || {}
  return url && title ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      css={cardCss}
      name="wiki-article"
      aria-label={`Wiki article about ${title}`}
      tabIndex={0}
    >
      <DataBlock
        heading={<Ellipsis>{title}</Ellipsis>}
        data={
          <Ellipsis lineClamp={3} noTooltip>
            <Paragraph size="xs">{description}</Paragraph>
          </Ellipsis>
        }
      />
    </a>
  ) : (
    <Paragraph size="xs">{transl.noWikiArticle()}</Paragraph>
  )
}

function LoadingSkeleton() {
  return (
    <div css={cardCss}>
      <Separator size="xxs" />
      <Skeleton.H5 />
      <Separator size="xs" />
      <Skeleton.Paragraph size="xs" lineCount={2} />
      <Separator size="xxs" />
    </div>
  )
}

function ErrorCard() {
  return (
    <div css={cardCss}>
      <Paragraph size="xs">{transl.errorSubtitle()}</Paragraph>
    </div>
  )
}

export default function WikiLink({ place = null, loading }) {
  const {
    data: wikiData,
    loading: wikiLinkLoading,
    error: fetchError,
  } = useQuery(wikiLinkQuery, {
    variables: { placeIds: [place?.id] },
    skip: !place?.id,
  })

  const View = useCallback(() => {
    if (fetchError) return <ErrorCard />
    if (wikiLinkLoading || loading) return <LoadingSkeleton />
    if (wikiData) return <WikiCard wikiData={wikiData} />
    if (!wikiData && !loading && !fetchError) return <ErrorCard />
    return <LoadingSkeleton />
  }, [fetchError, loading, wikiData, wikiLinkLoading])

  return (
    <>
      <Divider />
      <Separator size="xs" />
      <H3 size="H6">{transl.heading()}</H3>
      <View />
      <Separator size="nano" />
      <div css={greyText}>
        <Paragraph size="xs">{transl.subHeading()}</Paragraph>
      </div>
      <Divider />
    </>
  )
}
